import React, { useState, useMemo, useRef } from 'react'
import { Calculator, Download, AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import type { ChekiOrder, ChekiForm } from '@/hooks/use-cheki'
import { formatCurrency, cn } from '@/lib/utils'

// ── Types ─────────────────────────────────────────────────────────────────────

type SplitMode = 'percent' | 'fixed'

// Each party has separate values for PO and OTS orders
interface PartyValues {
  po: number
  ots: number
}

interface DistributionConfig {
  mode: SplitMode  // global — applies to all three parties at once
  member: PartyValues
  management: PartyValues
  photographer: PartyValues & { name: string }
}

interface MemberCalcRow {
  memberId: string
  memberName: string
  poQty: number
  otsQty: number
  quantity: number   // poQty + otsQty
  grossTotal: number
  memberCut: number
  managementCut: number
  photographerCut: number
}

interface KalkulasiSummary {
  rows: MemberCalcRow[]
  totalQuantity: number
  totalGross: number
  totalMemberCut: number
  totalManagementCut: number
  totalPhotographerCut: number
  hasRoundingError: boolean
}

const DEFAULT_CONFIG: DistributionConfig = {
  mode: 'percent',
  member:       { po: 60, ots: 60 },
  management:   { po: 30, ots: 30 },
  photographer: { po: 10, ots: 10, name: 'Fotografer' },
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function resolveOrderUnitPrice(order: ChekiOrder, form: ChekiForm): number {
  if (order.is_ots) return form.price_ots_idr ?? form.price_idr
  return form.price_idr
}

function computeCut(mode: SplitMode, value: number, qty: number, gross: number): number {
  if (mode === 'percent') return Math.round(gross * (value / 100))
  return value * qty
}

function computeKalkulasi(
  orders: ChekiOrder[],
  form: ChekiForm,
  config: DistributionConfig,
): KalkulasiSummary {
  const eligible = orders.filter((o) => o.status === 'verified' && !o.is_group_cheki)

  // Track PO and OTS buckets separately per member
  const memberMap = new Map<string, {
    name: string
    poQty: number
    poGross: number
    otsQty: number
    otsGross: number
  }>()

  for (const order of eligible) {
    const key = order.member_id ?? '__no_member__'
    const name = order.member_id
      ? (order.idol_members?.stage_name ?? order.idol_members?.name ?? 'Member Tidak Dikenal')
      : 'Tanpa Member'

    const existing = memberMap.get(key) ?? { name, poQty: 0, poGross: 0, otsQty: 0, otsGross: 0 }
    const unitPrice = resolveOrderUnitPrice(order, form)
    const qty = order.quantity ?? 1

    if (order.is_ots) {
      memberMap.set(key, { ...existing, name, otsQty: existing.otsQty + qty, otsGross: existing.otsGross + unitPrice * qty })
    } else {
      memberMap.set(key, { ...existing, name, poQty: existing.poQty + qty, poGross: existing.poGross + unitPrice * qty })
    }
  }

  const rows: MemberCalcRow[] = []

  for (const [memberId, data] of memberMap.entries()) {
    // Apply PO config to PO orders, OTS config to OTS orders
    const memberCut =
      computeCut(config.mode, config.member.po, data.poQty, data.poGross) +
      computeCut(config.mode, config.member.ots, data.otsQty, data.otsGross)
    const managementCut =
      computeCut(config.mode, config.management.po, data.poQty, data.poGross) +
      computeCut(config.mode, config.management.ots, data.otsQty, data.otsGross)
    const photographerCut =
      computeCut(config.mode, config.photographer.po, data.poQty, data.poGross) +
      computeCut(config.mode, config.photographer.ots, data.otsQty, data.otsGross)

    rows.push({
      memberId,
      memberName: data.name,
      poQty: data.poQty,
      otsQty: data.otsQty,
      quantity: data.poQty + data.otsQty,
      grossTotal: data.poGross + data.otsGross,
      memberCut,
      managementCut,
      photographerCut,
    })
  }

  rows.sort((a, b) => {
    if (a.memberId === '__no_member__') return 1
    if (b.memberId === '__no_member__') return -1
    return a.memberName.localeCompare(b.memberName, 'id')
  })

  const totalQuantity = rows.reduce((s, r) => s + r.quantity, 0)
  const totalGross = rows.reduce((s, r) => s + r.grossTotal, 0)
  const totalMemberCut = rows.reduce((s, r) => s + r.memberCut, 0)
  const totalManagementCut = rows.reduce((s, r) => s + r.managementCut, 0)
  const totalPhotographerCut = rows.reduce((s, r) => s + r.photographerCut, 0)

  const cutSum = totalMemberCut + totalManagementCut + totalPhotographerCut
  const hasRoundingError = Math.abs(cutSum - totalGross) > 1

  return {
    rows,
    totalQuantity,
    totalGross,
    totalMemberCut,
    totalManagementCut,
    totalPhotographerCut,
    hasRoundingError,
  }
}

interface PercentValidation {
  poError: string | null
  otsError: string | null
}

function validatePercentConfig(config: DistributionConfig): PercentValidation {
  if (config.mode !== 'percent') return { poError: null, otsError: null }

  const poSum = config.member.po + config.management.po + config.photographer.po
  const otsSum = config.member.ots + config.management.ots + config.photographer.ots

  return {
    poError: Math.abs(poSum - 100) > 0.01
      ? `Total PO ${poSum.toFixed(1)}% — harus tepat 100%`
      : null,
    otsError: Math.abs(otsSum - 100) > 0.01
      ? `Total OTS ${otsSum.toFixed(1)}% — harus tepat 100%`
      : null,
  }
}

interface FixedPriceCheck {
  label: string
  price: number
  diff: number  // total - price: positive = over, negative = under, 0 = exact
}

function getFixedValidation(
  config: DistributionConfig,
  priceIdr: number,
  priceOtsIdr: number | null,
): FixedPriceCheck[] {
  if (config.mode !== 'fixed') return []
  const poTotal = config.member.po + config.management.po + config.photographer.po
  const checks: FixedPriceCheck[] = [
    { label: 'PO', price: priceIdr, diff: poTotal - priceIdr },
  ]
  if (priceOtsIdr != null) {
    const otsTotal = config.member.ots + config.management.ots + config.photographer.ots
    checks.push({ label: 'OTS', price: priceOtsIdr, diff: otsTotal - priceOtsIdr })
  }
  return checks
}

function sanitizeFilename(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_\-]/g, '')
    .slice(0, 50)
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => {
      if (b) resolve(b)
      else reject(new Error('Canvas toBlob failed'))
    }, 'image/png')
  })
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ── Distribution Config Card ──────────────────────────────────────────────────

interface DistributionConfigCardProps {
  config: DistributionConfig
  onChange: (config: DistributionConfig) => void
  priceIdr: number
  priceOtsIdr: number | null
}

function DistributionConfigCard({ config, onChange, priceIdr, priceOtsIdr }: DistributionConfigCardProps) {
  const { poError, otsError } = validatePercentConfig(config)
  const fixedChecks = getFixedValidation(config, priceIdr, priceOtsIdr)
  const isPercent = config.mode === 'percent'
  const suffix = isPercent ? '%' : 'Rp/lbr'
  const numMax = isPercent ? 100 : undefined
  const showOts = priceOtsIdr != null  // only show OTS column when OTS price is configured

  function numInput(
    value: number,
    onCommit: (v: number) => void,
  ) {
    return (
      <input
        type="number"
        min={0}
        max={numMax}
        value={value}
        onChange={(e) => onCommit(parseFloat(e.target.value) || 0)}
        className="w-24 rounded-md border border-border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
      />
    )
  }

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
      {/* Header + global mode toggle */}
      <div className="flex items-center justify-between gap-4">
        <h3 className="text-sm font-semibold">Konfigurasi Pembagian</h3>
        <div className="flex rounded-md border border-border overflow-hidden shrink-0">
          <button
            type="button"
            onClick={() => onChange({ ...config, mode: 'percent' })}
            className={cn(
              'px-3 py-1.5 text-xs font-medium transition-colors',
              isPercent
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent',
            )}
          >
            %
          </button>
          <button
            type="button"
            onClick={() => onChange({ ...config, mode: 'fixed' })}
            className={cn(
              'px-3 py-1.5 text-xs font-medium transition-colors border-l border-border',
              !isPercent
                ? 'bg-primary text-primary-foreground'
                : 'bg-background text-muted-foreground hover:bg-accent',
            )}
          >
            Rp/lbr
          </button>
        </div>
      </div>

      {/* Column headers */}
      <div className="grid items-center gap-3" style={{ gridTemplateColumns: showOts ? '7rem 1fr 1fr' : '7rem 1fr' }}>
        <span />
        <span className="text-xs font-medium text-muted-foreground text-center">
          PO&nbsp;
          <span className="font-normal">({formatCurrency(priceIdr)}/lbr)</span>
        </span>
        {showOts && (
          <span className="text-xs font-medium text-muted-foreground text-center">
            OTS&nbsp;
            <span className="font-normal">({formatCurrency(priceOtsIdr!)}/lbr)</span>
          </span>
        )}
      </div>

      {/* Per-party value rows */}
      <div className="space-y-3">
        {/* Member */}
        <div className="grid items-center gap-3" style={{ gridTemplateColumns: showOts ? '7rem 1fr 1fr' : '7rem 1fr' }}>
          <span className="text-sm font-medium">Member</span>
          <div className="flex items-center gap-1.5">
            {numInput(config.member.po, (v) => onChange({ ...config, member: { ...config.member, po: v } }))}
            <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>
          </div>
          {showOts && (
            <div className="flex items-center gap-1.5">
              {numInput(config.member.ots, (v) => onChange({ ...config, member: { ...config.member, ots: v } }))}
              <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>
            </div>
          )}
        </div>

        {/* Management */}
        <div className="grid items-center gap-3" style={{ gridTemplateColumns: showOts ? '7rem 1fr 1fr' : '7rem 1fr' }}>
          <span className="text-sm font-medium">Management</span>
          <div className="flex items-center gap-1.5">
            {numInput(config.management.po, (v) => onChange({ ...config, management: { ...config.management, po: v } }))}
            <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>
          </div>
          {showOts && (
            <div className="flex items-center gap-1.5">
              {numInput(config.management.ots, (v) => onChange({ ...config, management: { ...config.management, ots: v } }))}
              <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>
            </div>
          )}
        </div>

        {/* Fotografer */}
        <div className="grid items-center gap-3" style={{ gridTemplateColumns: showOts ? '7rem 1fr 1fr' : '7rem 1fr' }}>
          <span className="text-sm font-medium">Fotografer</span>
          <div className="flex items-center gap-1.5">
            {numInput(config.photographer.po, (v) =>
              onChange({ ...config, photographer: { ...config.photographer, po: v } })
            )}
            <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>
          </div>
          {showOts && (
            <div className="flex items-center gap-1.5">
              {numInput(config.photographer.ots, (v) =>
                onChange({ ...config, photographer: { ...config.photographer, ots: v } })
              )}
              <span className="text-xs text-muted-foreground shrink-0">{suffix}</span>
            </div>
          )}
        </div>

        {/* Fotografer name (full width below) */}
        <div className="flex items-center gap-3">
          <span className="w-28 shrink-0 text-xs text-muted-foreground">Nama Fotografer</span>
          <input
            type="text"
            value={config.photographer.name}
            onChange={(e) =>
              onChange({ ...config, photographer: { ...config.photographer, name: e.target.value } })
            }
            placeholder="Nama fotografer"
            className="flex-1 rounded-md border border-border bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {/* Percent mode: sum ≠ 100% warnings */}
      {(poError || otsError) && (
        <div className="space-y-1.5">
          {poError && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {poError}
            </div>
          )}
          {otsError && (
            <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              {otsError}
            </div>
          )}
        </div>
      )}

      {/* Fixed mode: compare totals against prices */}
      {!isPercent && fixedChecks.length > 0 && (
        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 space-y-2">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Info className="h-3.5 w-3.5 shrink-0" />
            <span>Cek total alokasi vs harga cheki</span>
          </div>
          {fixedChecks.map((check) => {
            const isExact = check.diff === 0
            const isOver = check.diff > 0
            const total = check.label === 'PO'
              ? config.member.po + config.management.po + config.photographer.po
              : config.member.ots + config.management.ots + config.photographer.ots
            return (
              <div
                key={check.label}
                className={cn(
                  'flex items-center justify-between rounded px-2 py-1 text-xs',
                  isExact
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200',
                )}
              >
                <span className="flex items-center gap-1.5">
                  {isExact
                    ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                    : <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
                  {check.label}: total {formatCurrency(total)} vs harga {formatCurrency(check.price)}
                </span>
                <span className="font-medium tabular-nums">
                  {isExact
                    ? 'Sesuai ✓'
                    : isOver
                      ? `+${formatCurrency(check.diff)}`
                      : `${formatCurrency(check.diff)}`}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Kalkulasi Table ───────────────────────────────────────────────────────────

function KalkulasiTable({
  summary,
  photographerName,
}: {
  summary: KalkulasiSummary
  photographerName: string
}) {
  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/50">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Member</th>
              <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Cheki Terjual</th>
              <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Total Bruto</th>
              <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Bagian Member</th>
              <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Bagian Management</th>
              <th className="px-4 py-3 text-right font-medium whitespace-nowrap">
                Bagian {photographerName || 'Fotografer'}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {summary.rows.map((row) => (
              <tr key={row.memberId} className="bg-card hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 font-medium">{row.memberName}</td>
                <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                  {row.quantity} lbr
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(row.grossTotal)}</td>
                <td className="px-4 py-3 text-right tabular-nums text-green-700 font-medium">
                  {formatCurrency(row.memberCut)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-blue-700 font-medium">
                  {formatCurrency(row.managementCut)}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-purple-700 font-medium">
                  {formatCurrency(row.photographerCut)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-muted/50 font-semibold">
              <td className="px-4 py-3">TOTAL</td>
              <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                {summary.totalQuantity} lbr
              </td>
              <td className="px-4 py-3 text-right tabular-nums">{formatCurrency(summary.totalGross)}</td>
              <td className="px-4 py-3 text-right tabular-nums text-green-700">
                {formatCurrency(summary.totalMemberCut)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-blue-700">
                {formatCurrency(summary.totalManagementCut)}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-purple-700">
                {formatCurrency(summary.totalPhotographerCut)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {summary.hasRoundingError && (
        <p className="text-xs text-muted-foreground px-1">
          * Selisih pembulatan kecil mungkin terjadi pada mode campuran (% dan Rp).
        </p>
      )}
    </div>
  )
}

// ── Slip Components (hidden, captured by html2canvas) ─────────────────────────

const slipContainerStyle: React.CSSProperties = {
  width: '420px',
  padding: '28px',
  backgroundColor: '#ffffff',
  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  fontSize: '13px',
  color: '#111827',
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  boxSizing: 'border-box',
}

const slipTitleStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: '700',
  marginBottom: '2px',
  color: '#111827',
}

const slipSubtitleStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6b7280',
  marginBottom: '0',
}

const slipDividerStyle: React.CSSProperties = {
  borderTop: '1px solid #e5e7eb',
  margin: '14px 0',
}

const slipRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  marginBottom: '7px',
}

const slipLabelStyle: React.CSSProperties = {
  color: '#6b7280',
  fontSize: '12px',
  flexShrink: 0,
  marginRight: '8px',
}

const slipValueStyle: React.CSSProperties = {
  fontWeight: '600',
  textAlign: 'right' as const,
  wordBreak: 'break-word' as const,
}

const slipHighlightStyle: React.CSSProperties = {
  ...slipValueStyle,
  fontSize: '15px',
  color: '#059669',
}

const slipFootnoteStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#9ca3af',
  marginTop: '0',
}

const MemberSlip = React.forwardRef<
  HTMLDivElement,
  { row: MemberCalcRow; form: ChekiForm; generatedAt: Date }
>(({ row, form, generatedAt }, ref) => {
  const eventName = form.events?.title ?? ''
  const dateStr = generatedAt.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return (
    <div ref={ref} style={slipContainerStyle}>
      <div style={slipTitleStyle}>SLIP CHEKI — MEMBER</div>
      {eventName && <div style={slipSubtitleStyle}>{eventName}</div>}
      <div style={slipDividerStyle} />
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Form</span>
        <span style={slipValueStyle}>{form.title}</span>
      </div>
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Member</span>
        <span style={slipValueStyle}>{row.memberName}</span>
      </div>
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Tanggal Cetak</span>
        <span style={slipValueStyle}>{dateStr}</span>
      </div>
      <div style={slipDividerStyle} />
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Cheki Terjual</span>
        <span style={slipValueStyle}>{row.quantity} lembar</span>
      </div>
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Total Bruto</span>
        <span style={slipValueStyle}>{formatCurrency(row.grossTotal)}</span>
      </div>
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Bagian Management</span>
        <span style={slipValueStyle}>{formatCurrency(row.managementCut)}</span>
      </div>
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Bagian Fotografer</span>
        <span style={slipValueStyle}>{formatCurrency(row.photographerCut)}</span>
      </div>
      <div style={slipDividerStyle} />
      <div style={slipRowStyle}>
        <span style={{ ...slipLabelStyle, fontWeight: '600', color: '#111827', fontSize: '13px' }}>
          Diterima Member
        </span>
        <span style={slipHighlightStyle}>{formatCurrency(row.memberCut)}</span>
      </div>
      <div style={slipDividerStyle} />
      <div style={slipFootnoteStyle}>* Diterbitkan oleh sistem OneIdol</div>
    </div>
  )
})
MemberSlip.displayName = 'MemberSlip'

const PhotographerSlip = React.forwardRef<
  HTMLDivElement,
  { summary: KalkulasiSummary; config: DistributionConfig; form: ChekiForm; generatedAt: Date }
>(({ summary, config, form, generatedAt }, ref) => {
  const eventName = form.events?.title ?? ''
  const photographerName = config.photographer.name || 'Fotografer'
  const dateStr = generatedAt.toLocaleDateString('id-ID', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return (
    <div ref={ref} style={slipContainerStyle}>
      <div style={slipTitleStyle}>SLIP CHEKI — FOTOGRAFER</div>
      {eventName && <div style={slipSubtitleStyle}>{eventName}</div>}
      <div style={slipDividerStyle} />
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Form</span>
        <span style={slipValueStyle}>{form.title}</span>
      </div>
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Fotografer</span>
        <span style={slipValueStyle}>{photographerName}</span>
      </div>
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Tanggal Cetak</span>
        <span style={slipValueStyle}>{dateStr}</span>
      </div>
      <div style={slipDividerStyle} />
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Total Cheki (semua member)</span>
        <span style={slipValueStyle}>{summary.totalQuantity} lembar</span>
      </div>
      <div style={slipRowStyle}>
        <span style={slipLabelStyle}>Total Bruto</span>
        <span style={slipValueStyle}>{formatCurrency(summary.totalGross)}</span>
      </div>
      <div style={slipDividerStyle} />
      {summary.rows.map((row) => (
        <div key={row.memberId} style={slipRowStyle}>
          <span style={slipLabelStyle}>{row.memberName}</span>
          <span style={slipValueStyle}>{formatCurrency(row.photographerCut)}</span>
        </div>
      ))}
      <div style={slipDividerStyle} />
      <div style={slipRowStyle}>
        <span style={{ ...slipLabelStyle, fontWeight: '600', color: '#111827', fontSize: '13px' }}>
          Total Diterima Fotografer
        </span>
        <span style={{ ...slipHighlightStyle, color: '#7c3aed' }}>
          {formatCurrency(summary.totalPhotographerCut)}
        </span>
      </div>
      <div style={slipDividerStyle} />
      <div style={slipFootnoteStyle}>* Diterbitkan oleh sistem OneIdol</div>
    </div>
  )
})
PhotographerSlip.displayName = 'PhotographerSlip'

// ── Main Panel ────────────────────────────────────────────────────────────────

interface KalkulasiPanelProps {
  orders: ChekiOrder[]
  form: ChekiForm
}

export default function KalkulasiPanel({ orders, form }: KalkulasiPanelProps) {
  const [config, setConfig] = useState<DistributionConfig>(DEFAULT_CONFIG)
  const [isExporting, setIsExporting] = useState(false)

  const summary = useMemo(
    () => computeKalkulasi(orders, form, config),
    [orders, form, config],
  )

  // Maintain a stable ref array sized to rows.length + 1 (last slot = photographer)
  const slipRefs = useRef<Array<React.RefObject<HTMLDivElement>>>([])
  const neededCount = summary.rows.length + 1
  if (slipRefs.current.length !== neededCount) {
    slipRefs.current = Array.from(
      { length: neededCount },
      (_, i) => slipRefs.current[i] ?? React.createRef<HTMLDivElement>(),
    )
  }

  async function handleExport() {
    if (isExporting || summary.rows.length === 0) return
    setIsExporting(true)
    try {
      const html2canvas = (await import('html2canvas')).default
      const JSZip = (await import('jszip')).default
      const zip = new JSZip()

      const formTitle = sanitizeFilename(form.title) || 'cheki'
      // The slip components are already rendered (hidden), so refs are populated
      // Give browser a tick to ensure paint has completed
      await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 50)))

      for (let i = 0; i < summary.rows.length; i++) {
        const el = slipRefs.current[i]?.current
        if (!el) continue
        const canvas = await html2canvas(el, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          logging: false,
        })
        const blob = await canvasToBlob(canvas)
        const memberName = sanitizeFilename(summary.rows[i].memberName) || `member_${i + 1}`
        zip.file(`slip_${formTitle}_${memberName}.png`, blob)
      }

      // Photographer slip (last ref)
      const photoEl = slipRefs.current[summary.rows.length]?.current
      if (photoEl) {
        const canvas = await html2canvas(photoEl, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true,
          logging: false,
        })
        const blob = await canvasToBlob(canvas)
        const photographerName =
          sanitizeFilename(config.photographer.name || 'fotografer') || 'fotografer'
        zip.file(`slip_${formTitle}_fotografer_${photographerName}.png`, blob)
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' })
      triggerDownload(zipBlob, `slip-gaji_${formTitle}.zip`)
    } catch (err) {
      console.error('Export failed:', err)
    } finally {
      setIsExporting(false)
    }
  }

  const hasNoOrders = summary.rows.length === 0
  const photographerName = config.photographer.name || 'Fotografer'

  return (
    <div className="space-y-5">
      {/* Config */}
      <DistributionConfigCard
        config={config}
        onChange={setConfig}
        priceIdr={form.price_idr}
        priceOtsIdr={form.price_ots_idr}
      />

      {/* Empty state */}
      {hasNoOrders ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <Calculator className="mx-auto h-10 w-10 text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">
            Belum ada pesanan terverifikasi (non-group cheki) untuk dikalkulasi.
          </p>
        </div>
      ) : (
        <>
          {/* Calculation table */}
          <KalkulasiTable summary={summary} photographerName={photographerName} />

          {/* Export button */}
          <div className="flex justify-end">
            <button
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              <Download className="h-4 w-4" />
              {isExporting ? 'Membuat ZIP...' : 'Export Slip Gaji (ZIP)'}
            </button>
          </div>
        </>
      )}

      {/* Off-screen slip elements for html2canvas capture.
          Must NOT use visibility:hidden or display:none — html2canvas renders
          those as blank. Positioned far off-screen instead. */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: '-9999px',
          pointerEvents: 'none',
          zIndex: -1,
        }}
        aria-hidden="true"
      >
        {summary.rows.map((row, i) => (
          <MemberSlip
            key={row.memberId}
            ref={slipRefs.current[i]}
            row={row}
            form={form}
            generatedAt={new Date()}
          />
        ))}
        <PhotographerSlip
          ref={slipRefs.current[summary.rows.length]}
          summary={summary}
          config={config}
          form={form}
          generatedAt={new Date()}
        />
      </div>
    </div>
  )
}
